import { calculateHeaderGroupHeight } from '@theme/critical';
import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer
 * @property {HTMLElement} headerMenu
 * @property {HTMLElement} headerRowTop
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /** @type {number|null} */
  #menuDrawerHiddenWidth = null;
  /** @type {IntersectionObserver|null} */
  #intersectionObserver = null;

  #offscreen = false;
  #lastScrollTop = 0;
  #timeout = null;
  #animationDelay = 150;

  // rAF throttle
  #ticking = false;

  // Stabilize menu switching
  #mq = window.matchMedia('(max-width: 960px)');

  // Debounced theme-color setter (avoids heavy repaints mid-scroll)
  #setThemeColor = (el) => {
    const ric = window.requestIdleCallback || ((cb) => setTimeout(cb, 0));
    ric(() => changeMetaThemeColor(el));
  };

  // Observe only the header element; keep --header-height accurate
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;
    const { height } = entry.target.getBoundingClientRect();
    document.body.style.setProperty('--header-height', `${height}px`);

    // If drawer took over earlier and we're now on desktop, restore menu
    if (this.#menuDrawerHiddenWidth && !this.#mq.matches) {
      this.#updateMenuVisibility(false);
    }
  });

  // Less flappy sticky observation (iOS URL bar wiggle tolerant)
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      const isIntersecting = entry.isIntersecting;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        this.#setThemeColor(this.refs.headerRowTop);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, {
      threshold: 0,
      rootMargin: '-2px 0px 0px 0px',
    });

    this.#intersectionObserver.observe(this);
  };

  // Raw scroll listener -> rAF
  #onScrollRaw = () => {
    if (this.#ticking) return;
    this.#ticking = true;
    requestAnimationFrame(() => {
      this.#ticking = false;
      this.#handleWindowScroll();
    });
  };

  /**
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  #updateMenuVisibility(hideMenu) {
    const drawer = this.refs.headerDrawerContainer;
    const menu = this.refs.headerMenu;
    if (hideMenu) {
      drawer.classList.remove('desktop:hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
      menu.classList.add('hidden');
    } else {
      drawer.classList.add('desktop:hidden');
      this.#menuDrawerHiddenWidth = null;
      menu.classList.remove('hidden');
    }
  }

  #handleWindowScroll = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    // READS first (single pass)
    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = scrollTop <= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      // WRITES
      this.dataset.scrollDirection = isAtTop ? 'none' : (isScrollingUp ? 'up' : 'down');
      this.#lastScrollTop = scrollTop;
      return;
    }

    // scroll-up behavior
    if (isScrollingUp) {
      this.removeAttribute('data-animating');

      if (isAtTop) {
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';
      this.setAttribute('data-animating', '');
      this.#timeout = setTimeout(() => {
        this.dataset.stickyState = 'idle';
        this.removeAttribute('data-animating');
      }, this.#animationDelay);
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();

    // Keep header height CSS var fresh
    this.#resizeObserver.observe(this);

    // Menu overflow signal
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum, { passive: true });

    // Sticky modes
    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        // Passive + rAF throttling (iOS-friendly)
        document.addEventListener('scroll', this.#onScrollRaw, { passive: true });
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#onScrollRaw);
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');

  if (headerGroup) {
    // Observe the group (children size changes bubble to it) – cheaper than observing each child
    const ro = new ResizeObserver(() => {
      calculateHeaderGroupHeight(header, headerGroup);
    });
    // @ts-ignore: box option supported in modern browsers
    ro.observe(headerGroup, { box: 'border-box' });

    // If children are added/removed, recalc height once per frame
    let ticking = false;
    const mo = new MutationObserver(() => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        calculateHeaderGroupHeight(header, headerGroup);
      });
    });
    mo.observe(headerGroup, { childList: true, subtree: false });
  }
});
